import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../../config';
import './databases.css';

function CustomDatabase({ tableName, columns }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [formData, setFormData] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('asc');

  // Derive column db names from display names
  const colDefs = (columns || []).map(col => ({
    display: col.name,
    key: col.name.toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/^_|_$/g, ''),
    type: col.type || 'text',
  }));

  useEffect(() => { fetchRows(); }, [tableName]);

  const fetchRows = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/custom-databases/${tableName}/rows`);
      if (!res.ok) throw new Error('Failed to fetch data');
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    try {
      const url = editRow
        ? `${API_BASE_URL}/api/custom-databases/${tableName}/rows/${editRow.id}`
        : `${API_BASE_URL}/api/custom-databases/${tableName}/rows`;
      const res = await fetch(url, {
        method: editRow ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      setShowModal(false);
      setEditRow(null);
      setFormData({});
      await fetchRows();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/custom-databases/${tableName}/rows/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete');
      await fetchRows();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditRow(null);
    const empty = {};
    colDefs.forEach(c => { empty[c.key] = ''; });
    setFormData(empty);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditRow(row);
    const data = {};
    colDefs.forEach(c => { data[c.key] = row[c.key] ?? ''; });
    setFormData(data);
    setShowModal(true);
  };

  const handleSort = (key) => {
    if (sortField === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(key); setSortDir('asc'); }
  };

  // Filter
  const filtered = rows.filter(row =>
    colDefs.some(c => String(row[c.key] || '').toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    if (!sortField) return 0;
    const va = a[sortField] ?? '';
    const vb = b[sortField] ?? '';
    const cmp = String(va).localeCompare(String(vb), undefined, { numeric: true });
    return sortDir === 'asc' ? cmp : -cmp;
  });

  // Paginate
  const totalPages = Math.max(1, Math.ceil(sorted.length / rowsPerPage));
  const startIdx = (currentPage - 1) * rowsPerPage;
  const paginated = sorted.slice(startIdx, startIdx + rowsPerPage);

  const renderInput = (col) => {
    const val = formData[col.key] ?? '';
    const onChange = (e) => setFormData(prev => ({ ...prev, [col.key]: e.target.value }));

    switch (col.type) {
      case 'number':
      case 'currency':
        return <input type="number" step="any" value={val} onChange={onChange} placeholder={col.display} />;
      case 'date':
        return <input type="date" value={val} onChange={onChange} />;
      case 'boolean':
        return (
          <select value={val} onChange={onChange}>
            <option value="">—</option>
            <option value="1">Yes</option>
            <option value="0">No</option>
          </select>
        );
      case 'email':
        return <input type="email" value={val} onChange={onChange} placeholder={col.display} />;
      case 'url':
        return <input type="url" value={val} onChange={onChange} placeholder="https://..." />;
      default:
        return <input type="text" value={val} onChange={onChange} placeholder={col.display} />;
    }
  };

  const formatCell = (col, value) => {
    if (value === null || value === undefined || value === '') return '—';
    if (col.type === 'currency') return `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    if (col.type === 'boolean') return value === 1 || value === '1' ? 'Yes' : 'No';
    return String(value);
  };

  return (
    <div className="notion-card">
      <div className="notion-header">
        <h1 className="notion-title">Custom Database</h1>
        <div className="notion-actions">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            placeholder="🔍 Search..."
            className="search"
          />
          <button className="notion-button" onClick={() => handleSort(sortField || (colDefs[0] && colDefs[0].key))}>
            Sort {sortDir === 'asc' ? '↑' : '↓'}
          </button>
          <button className="notion-button notion-button-primary" onClick={openAdd} disabled={loading}>+ New Record</button>
        </div>
      </div>

      {error && <div className="error">{error}</div>}
      {loading && <div className="loading">Loading...</div>}

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="notion-modal-title">{editRow ? 'Edit Record' : 'New Record'}</h2>
            <div className="notion-form">
              {colDefs.map(col => (
                <div key={col.key} className="form-row">
                  <label>{col.display}</label>
                  {renderInput(col)}
                </div>
              ))}
              <div className="form-actions">
                <button className="notion-button notion-button-primary" onClick={handleSave} disabled={loading}>
                  {loading ? 'Saving...' : 'Save'}
                </button>
                <button className="notion-button" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="notion-table-container">
        <table className="notion-table">
          <thead>
            <tr>
              {colDefs.map(col => (
                <th key={col.key} className="notion-th" style={{ cursor: 'pointer' }} onClick={() => handleSort(col.key)}>
                  {col.display} {sortField === col.key ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </th>
              ))}
              <th className="notion-th" style={{ width: '100px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map(row => (
              <tr key={row.id} className="notion-row">
                {colDefs.map(col => (
                  <td key={col.key} className="notion-td">{formatCell(col, row[col.key])}</td>
                ))}
                <td className="notion-td">
                  <button onClick={() => openEdit(row)} className="notion-action" disabled={loading}>Edit</button>
                  <button onClick={() => handleDelete(row.id)} className="notion-action" style={{ marginLeft: 6, color: '#dc3545' }} disabled={loading}>Del</button>
                </td>
              </tr>
            ))}
            {paginated.length === 0 && !loading && (
              <tr>
                <td colSpan={colDefs.length + 1} className="notion-td" style={{ textAlign: 'center', padding: 0, border: 'none' }}>
                  <div className="db-empty-state" style={{ border: 'none', borderRadius: 0 }}>
                    <div className="db-empty-state-icon">📁</div>
                    <div className="db-empty-state-title">No records yet</div>
                    <div className="db-empty-state-text">Add your first record to start using this database.</div>
                    <button className="db-empty-state-btn" onClick={openAdd}>+ New Record</button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div className="db-pagination">
          <div className="db-pagination-left">
            <label>Rows per page:</label>
            <select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
          <div className="db-pagination-center">
            Showing {sorted.length === 0 ? 0 : startIdx + 1} to {Math.min(startIdx + rowsPerPage, sorted.length)} of {sorted.length} records
          </div>
          <div className="db-pagination-right">
            <button className="db-pagination-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) pageNum = i + 1;
              else if (currentPage <= 3) pageNum = i + 1;
              else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
              else pageNum = currentPage - 2 + i;
              return (
                <button key={pageNum} className={`db-pagination-btn ${currentPage === pageNum ? 'active' : ''}`} onClick={() => setCurrentPage(pageNum)}>{pageNum}</button>
              );
            })}
            <button className="db-pagination-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}>Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CustomDatabase;
